import { useContext, useCallback } from 'react';
import _ from 'underscore';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { BudgetSuggestionTiers, API_VERSION } from '../model/constants';

const TELEMETRY_CLIENT_NAME = 'bingadsweb[incontextrecommendation]';

const formatCustomBudgetPerformanceMetrics = ({ performanceMetricsResponse, apiVersion, isExpansionPilotEnabled }) => {
  const template = {};

  template.CustomBudgetPerformance = JSON.stringify(performanceMetricsResponse);
  template.isRetail = apiVersion === API_VERSION.V2;
  template.isExpansionPilotEnabled = !!isExpansionPilotEnabled;

  return template;
};

const formatBudgetSuggestionResponse = ({ budgetResponse }) => {
  const template = {};

  _.each(budgetResponse, (value, tier) => {
    const { BudgetAmountMonthly, BudgetAmountDaily, BudgetDetails } = value;

    template[`${tier}BudgetAmountMonthly`] = BudgetAmountMonthly;
    template[`${tier}BudgetAmountDaily`] = BudgetAmountDaily;
    template[`${tier}BudgetPerformance`] = JSON.stringify(BudgetDetails);
  });

  return template;
};

const formatBudgetTemplate = ({
  campaign,
  budgetResponse,
  appliedBudget,
  budgetTier,
  apiVersion = API_VERSION.V2,
  isExpansionPilotEnabled = true,
}) => {
  const {
    Id, Budget, BiddingScheme, CampaignType,
  } = campaign;

  const formattedBudgetResponse = formatBudgetSuggestionResponse({ budgetResponse });

  let tier = BudgetSuggestionTiers[budgetTier];

  if (budgetTier === 'CustomBudget') {
    tier = 'CustomBudget';
  }

  return {
    CampaignID: Id,
    CurrentBudget: Budget,
    BiddingStrategy: BiddingScheme,
    CampaignType,
    ...formattedBudgetResponse,
    BudgetApplied: appliedBudget,
    BudgetTier: tier,
    isRetail: apiVersion === API_VERSION.V2,
    isExpansionPilotEnabled: !!isExpansionPilotEnabled,
  };
};

const formatBudgetLandscapeTemplate = ({ campaign, budgetLandscapeResponse }) => {
  const { Id } = campaign;

  const template = formatBudgetSuggestionResponse({ budgetResponse: budgetLandscapeResponse });

  return {
    CampaignID: Id,
    ...template,
  };
};

export const useBudgetMetricsLogger = () => {
  const activity = useContext(ActivityContext);

  const logBudgetFetchMetrics = useCallback(({
    campaign,
    budgetResponse,
    apiVersion,
    isExpansionPilotEnabled,
  }) => {
    if (activity) {
      activity.trace(`[BudgetSuggestionV2][BudgetSuggestion] - ${JSON.stringify(formatBudgetTemplate({
        campaign, budgetResponse, apiVersion, isExpansionPilotEnabled,
      }))}`);
    }
  }, [activity]);

  const logCustomBudgetPerformanceMetrics = useCallback(({
    campaign,
    performanceMetricsResponse,
    apiVersion,
    isExpansionPilotEnabled,
  }) => {
    if (activity) {
      activity.trace(`[BudgetSuggestionV2][PerformanceMetrics] - ${JSON.stringify(formatCustomBudgetPerformanceMetrics({
        campaign, performanceMetricsResponse, apiVersion, isExpansionPilotEnabled,
      }))}`);
    }
  }, [activity]);

  const logBudgetLandscapeMetrics = useCallback(({
    campaign,
    budgetLandscapeResponse,
  }) => {
    if (activity) {
      activity.trace(`[BudgetSuggestionV2][BudgetLandscape] - ${JSON.stringify(formatBudgetLandscapeTemplate({ campaign, budgetLandscapeResponse }))}`);
    }
  }, [activity]);

  const saveBudgetSuggestionMetrics = useCallback(({
    campaign,
    budgetResponse,
    appliedBudget,
    budgetTier,
    apiVersion,
    isExpansionPilotEnabled,
  }) => {
    if (budgetTier === null && activity) {
      activity.trace(`[BudgetSuggestionV2][BudgetApplied][ManualInput] - ${JSON.stringify(formatBudgetTemplate({
        campaign,
        budgetResponse,
        appliedBudget,
        budgetTier,
        apiVersion,
        isExpansionPilotEnabled,
      }))}`);
    } else if (activity) {
      activity.trace(`[BudgetSuggestionV2][BudgetApplied] - ${JSON.stringify(formatBudgetTemplate({
        campaign,
        budgetResponse,
        appliedBudget,
        budgetTier,
        apiVersion,
        isExpansionPilotEnabled,
      }))}`);
    }
  }, [activity]);

  const logControlVsPilotMetricsForSearchAndShopping = useCallback(({
    CampaignID,
    CampaignType,
    DidSeeBudgetSuggestion,
    IsUsingV2Endpoint,
  }) => {
    if (activity) {
      activity.trace(`[BudgetSuggestionV2][ControlVsPilot] - ${JSON.stringify({
        CampaignID,
        CampaignType,
        DidSeeBudgetSuggestion,
        ...(IsUsingV2Endpoint !== undefined && { IsUsingV2Endpoint }),
      })}`);
    }
  }, [activity]);

  // @aiContributed-MAPFuse-2026-05-12
  const logBudgetSuggestionView = useCallback(({
    campaignId, optId, suggestedBudget, currency,
  }) => {
    if (activity) {
      activity.trace(
        `View inline budget suggestion success for CampaignId:${campaignId}, ClientName:${TELEMETRY_CLIENT_NAME}, BudgetSuggestionId:${optId}, SuggestedBudget:${suggestedBudget}, Currency:${currency}`,
        'InlineBudgetEditor/ViewBudget'
      );
    }
  }, [activity]);

  const logBudgetSuggestionAdoption = useCallback(({
    campaignId, optId, suggestedBudget, savedBudget, currency,
  }) => {
    if (activity) {
      activity.trace(
        `Apply inline budget suggestion success for CampaignId:${campaignId}, ClientName:${TELEMETRY_CLIENT_NAME}, BudgetSuggestionId:${optId}, SuggestedBudget:${suggestedBudget}, SavedBudget:${savedBudget}, Currency:${currency}`,
        'InlineBudgetEditor/ApplyBudget'
      );
    }
  }, [activity]);

  const logNotifyAdInsightResult = useCallback((success, error) => {
    if (activity) {
      if (success) {
        activity.trace('[BudgetSuggestionV2] Apply budget landscape success and notify ad insight finished');
      } else {
        activity.trace(`[BudgetSuggestionV2] Apply budget landscape success but notify ad insight fail: ${JSON.stringify(error)}`);
      }
    }
  }, [activity]);

  return {
    logBudgetFetchMetrics,
    logCustomBudgetPerformanceMetrics,
    logBudgetLandscapeMetrics,
    saveBudgetSuggestionMetrics,
    logControlVsPilotMetricsForSearchAndShopping,
    logBudgetSuggestionView,
    logBudgetSuggestionAdoption,
    logNotifyAdInsightResult,
  };
};
